import React, { useState, useRef, useEffect } from 'react';
import { validateDocumentUpload, isUploadDisabled } from '../../utils/documentValidation';
import { DOCUMENT_CONSTANTS } from '../../utils/constants/MilestoneConstants';
import { useDispatch } from 'react-redux';
import { uplaodDocument } from '../../redux/actions/documentApi';
import {
    applyOptimisticSelection,
    collectValidFilesToUpload,
    createFileLikeFromDocument,
    createFileUploadTask,
    getErrorFileKey,
    getSelectedFileKey,
    getUploadDisabledStyle,
    shouldShowDeleteFileAction,
    shouldShowFileNameChip,
} from './FormUpload.helpers';


const ensureFileWithMetadata = (entry) => {
    if (!entry) {
        return null;
    }

    if (entry instanceof File) {
        if (!entry.file) {
            entry.file = entry;
        }
        if (!entry.file_name) {
            entry.file_name = entry.name;
        }
        return entry;
    }

    if (entry?.file instanceof File) {
        const rawFile = entry.file;
        rawFile.file = rawFile;
        rawFile.isUploading = entry.isUploading ?? rawFile.isUploading;
        rawFile.document_ulid = entry.document_ulid ?? rawFile.document_ulid;
        rawFile.file_name = entry.file_name ?? rawFile.file_name ?? rawFile.name;
        rawFile.created_at = entry.created_at ?? rawFile.created_at;
        // Note: lastModified is read-only, so we don't set it
        rawFile.type = entry.type ?? rawFile.type;
        return rawFile;
    }

    // Handle document objects (from API) that have document_ulid but aren't File objects
    if (entry.document_ulid || entry.ulid) {
        return createFileLikeFromDocument(entry);
    }

    return null;
};

const normalizeFileList = (list) => {
    if (!list) {
        return [];
    }
    const array = Array.isArray(list) ? list : [list];
    return array
        .map((item) => ensureFileWithMetadata(item))
        .filter((item) => item instanceof File);
};

const DropZoneHint = ({ isDragging, uploadDisabled, maxDocuments }) => {
    if (isDragging) {
        return <p className='font-b2 my-2'>Drop the files here...</p>;
    }
    if (uploadDisabled) {
        return (
            <>
                <p className='font-b2 my-2'>Maximum {maxDocuments} files uploaded</p>
                <p className='font-b3 my-2'>Remove files to upload more</p>
            </>
        );
    }
    return (
        <>
            <p className='font-b2 my-2'>
                Drag & Drop or <span className='text-secondary-700 drag-and-drop-btn'>Browse</span> files to upload
            </p>
            <p className='font-b3 my-2'>Accept Format: pdf, jpeg, png up to 10MB</p>
        </>
    );
};

const UploadZone = ({ uploadType, id, isDragging, uploadDisabled, maxDocuments }) => {
    const inputId = id ? id : 'file-upload';
    const disabledStyle = getUploadDisabledStyle(uploadDisabled);

    if (uploadType === 'button') {
        return (
            <label
                htmlFor={inputId}
                className={`d-flex align-items-center justify-content-center secondary-btn secondary-btn-sm ${
                    isDragging ? 'dragging' : ''
                } ${uploadDisabled ? 'disabled' : ''}`}
                style={disabledStyle}>
                {uploadDisabled ? `Max ${maxDocuments} files` : 'Add Files'}
            </label>
        );
    }
    if (uploadType === 'uploadIcon') {
        return (
            <label htmlFor={inputId} style={disabledStyle}>
                {uploadDisabled ? `Max ${maxDocuments} files` : 'Upload'}
                <span className='sf-upload text-secondary-900 font-h1 cursor'></span>
            </label>
        );
    }
    return (
        <label
            htmlFor={inputId}
            className={`drop-zone ${isDragging ? 'dragging' : ''} ${uploadDisabled ? 'disabled' : ''}`}
            style={disabledStyle}>
            <i className='bi bi-file-earmark-plus'></i>
            <span className='font-b3 text-neutral-700'>
                <DropZoneHint isDragging={isDragging} uploadDisabled={uploadDisabled} maxDocuments={maxDocuments} />
            </span>
        </label>
    );
};

const UploadIconFileRow = ({ file, index, onRemove }) => (
    <p className='mb-0 p-1 bg-secondary-100 mb-1'>
        <i className='bi bi-file-earmark'>
            &nbsp;
            <span className='filename-text font-b4 pe-5'>{file?.name || file?.file?.name}</span>
        </i>
        <span className='sf-close font-h2 cursor' onClick={() => onRemove(index, file)}></span>
    </p>
);

const FileDetailsRow = ({
    file,
    index,
    isDeletedFile,
    multiple,
    isEditable,
    documentUlid,
    handleRemove,
    handleDeleteFile,
}) => (
    <div className='d-flex align-items-center file-div-wrap py-2'>
        {shouldShowFileNameChip({ file, isDeletedFile, multiple }) ? (
            <span className='d-flex p-2 justify-content-between rounded align-items-center file-div'>
                {file.isUploading && (
                    <span className='spinner-border spinner-border-sm me-2' role='status' aria-hidden='true'></span>
                )}
                <span className='bi bi-file-earmark'>
                    &nbsp;
                    <span className='filename-text font-b3 pe-5'>{file?.name || file?.file_name || file?.file?.name}</span>
                </span>
            </span>
        ) : null}
        {isEditable && isDeletedFile && !multiple && (
            <span
                className='text-secondary-700 remove-btn font-b3 cursor ms-3'
                onClick={() => handleDeleteFile(index, documentUlid, false)}>
                Undo <span className='sf-reset'></span>
            </span>
        )}
        {shouldShowDeleteFileAction({ isEditable, isDeletedFile, multiple, file }) ? (
            <span
                className='text-secondary-700 remove-btn font-b3 cursor ms-3'
                onClick={() =>
                    multiple ? handleRemove(index, file) : handleDeleteFile(index, documentUlid, true)
                }>
                Delete File <span className='sf-delete'></span>
            </span>
        ) : null}
    </div>
);

const FormUpload = (props) => {
    const dispatch = useDispatch();
    const [selectedFiles, setSelectedFiles] = useState(() => {
        if (props.documentName && !props.selectedFiles && !props.fieldValue) {
            const placeholder = new File([], props.documentName);
            placeholder.file = placeholder;
            placeholder.file_name = props.documentName;
            placeholder.isUploading = false;
            return [placeholder];
        }
        return normalizeFileList(props.selectedFiles || props.fieldValue);
    });
    const [isDragging, setIsDragging] = useState(false);
    const fileInputRef = useRef(null);
    const [errors, setErrors] = useState([]);
    const [isDeletedFile, setIsDeletedFile] = useState(props.isDeletedFile ? props.isDeletedFile : false);

    const handleImageUpload = async (file) => {
        const formData = new FormData();
        formData.append('file', file);

        const result = await Promise.resolve(dispatch(uplaodDocument(formData)));

        // Redux Toolkit thunk returns action object: { payload, type, meta, error }
        // Check if the action was rejected
        if (result.type && result.type.endsWith('/rejected')) {
            const errorMessage = result.payload?.message ||
                result.payload?.serviceMessage ||
                result.error?.message ||
                result.payload ||
                'File upload failed';
            throw new Error(errorMessage);
        }

        // Check if payload exists and has error
        if (result.payload && (result.payload.error || result.payload.message)) {
            const errorMessage = result.payload.message || result.payload.error || 'File upload failed';
            throw new Error(errorMessage);
        }

        // Return the payload if successful
        return result.payload || result;
    };

    const handleFileChange = async (e) => {
        const files = Array.from(e.target.files);
        const newErrors = [];
        const uploadedPayloads = [];
        const maxDocuments = props.maxDocuments || DOCUMENT_CONSTANTS.MAX_DOCUMENTS_PER_PAYMENT;
        const validation = validateDocumentUpload(files, selectedFiles, maxDocuments);

        if (!validation.isValid) {
            setErrors([...errors, ...validation.errors]);
            return;
        }

        const validFilesToUpload = collectValidFilesToUpload(validation, newErrors, props.multiple);
        applyOptimisticSelection({
            validFilesToUpload,
            newErrors,
            errors,
            multiple: props.multiple,
            selectedFiles,
            setSelectedFiles,
            setSelectedFilesProp: props.setSelectedFiles,
            setErrors,
        });

        const uploadPromises = validFilesToUpload.map((file) =>
            createFileUploadTask({
                file,
                handleImageUpload,
                uploadedPayloads,
                setSelectedFiles,
                setSelectedFilesProp: props.setSelectedFiles,
                setErrors,
                matchFile: (f, current) => f === current || f.file === current,
            }),
        );

        Promise.all(uploadPromises).then((results) => {
            const successfulFiles = results.filter((r) => r.success && r.file).map((r) => r.file);
            if (successfulFiles.length > 0) {
                props.setUploadedFiles?.(uploadedPayloads);
                props.onChangeHandle?.(successfulFiles, uploadedPayloads, e);
            }
        });

        setIsDeletedFile(false);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
        e.preventDefault();
    };

    const handleRemove = (index, a) => {
        const hasError = errors?.find((err, i) => i === index);
        const ishasError = errors?.some((err, i) => err.error);

        if (hasError?.error) {
            const updatedErrors = [...errors];
            updatedErrors.splice(index, 1);
            setErrors(updatedErrors);
        } else {
            const updatedFiles = selectedFiles.filter((_, idx) => idx !== index);
            setSelectedFiles(updatedFiles);
            props.setSelectedFiles?.(updatedFiles);
            props.handleFormikRemove?.(updatedFiles);
        }
        props.onDeleteFile && props.onDeleteFile(props.documentUlid || index, ishasError);
    };

    const handleDrop = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);

        const maxDocuments = props.maxDocuments || DOCUMENT_CONSTANTS.MAX_DOCUMENTS_PER_PAYMENT;
        if (isUploadDisabled(selectedFiles, maxDocuments)) {
            return;
        }

        Array.from(e.dataTransfer.files);
        const newErrors = [];
        const uploadedPayloads = [];
        if (!e.dataTransfer.items) {
            return;
        }

        const files = Array.from(e.dataTransfer.items)
            .filter((item) => item.kind === 'file')
            .map((item) => item.getAsFile());
        const validation = validateDocumentUpload(files, selectedFiles, maxDocuments);

        if (!validation.isValid) {
            setErrors([...errors, ...validation.errors]);
            return;
        }

        const validFilesToUpload = collectValidFilesToUpload(validation, newErrors, props.multiple);
        applyOptimisticSelection({
            validFilesToUpload,
            newErrors,
            errors,
            multiple: props.multiple,
            selectedFiles,
            setSelectedFiles,
            setSelectedFilesProp: props.setSelectedFiles,
            setErrors,
        });

        const uploadPromises = validFilesToUpload.map((file) =>
            createFileUploadTask({
                file,
                handleImageUpload,
                uploadedPayloads,
                setSelectedFiles,
                setSelectedFilesProp: props.setSelectedFiles,
                setErrors,
                matchFile: (f, current) => f === current || f?.file === current,
            }),
        );

        Promise.all(uploadPromises).then((results) => {
            const successfulFiles = results.filter((r) => r.success && r.file).map((r) => r.file);
            if (successfulFiles.length > 0) {
                props.setUploadedFiles?.(uploadedPayloads);
                props.onChangeHandle?.(successfulFiles, uploadedPayloads, e);
            }
        });
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();

        // Only show drag over effect if upload is not disabled
        const maxDocuments = props.maxDocuments || DOCUMENT_CONSTANTS.MAX_DOCUMENTS_PER_PAYMENT;
        if (!isUploadDisabled(selectedFiles, maxDocuments)) {
            setIsDragging(true);
        }
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    };

    const handleDeleteFile = (index, id, state) => {
        if (!props.multiple) {
            setIsDeletedFile(state);
            props.onDeleteFile && props.onDeleteFile(id, state);
        }
    };


    useEffect(() => {
        props.getFileErrors && props.getFileErrors(errors);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [errors, props.getFileErrors]);

    // Sync with selectedFiles prop when it changes
    useEffect(() => {
        if (props.selectedFiles !== undefined) {
            setSelectedFiles(normalizeFileList(props.selectedFiles));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.selectedFiles]);

    // Sync with fieldValue prop when it changes (for Formik integration)
    useEffect(() => {
        if (props.fieldValue !== undefined && props.selectedFiles === undefined) {
            setSelectedFiles(normalizeFileList(props.fieldValue));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.fieldValue]);

    return (
        <div className=''>
            {props.uploadType === 'uploadIcon' ? null : <p className='font-b2 text-neutral-900 mb-1'>{props.title}</p>}

            <div onDrop={handleDrop} onDragOver={handleDragOver} onDragLeave={handleDragLeave}>
                <UploadZone
                    uploadType={props.uploadType}
                    id={props.id}
                    isDragging={isDragging}
                    uploadDisabled={isUploadDisabled(
                        selectedFiles,
                        props.maxDocuments || DOCUMENT_CONSTANTS.MAX_DOCUMENTS_PER_PAYMENT,
                    )}
                    maxDocuments={props.maxDocuments || DOCUMENT_CONSTANTS.MAX_DOCUMENTS_PER_PAYMENT}
                />
                <input
                    type='file'
                    id={props.id ? props.id : 'file-upload'}
                    multiple={props.multiple ? props.multiple : false}
                    onChange={props.type === 'iaapCertificate' ? props.onChange : handleFileChange}
                    ref={fileInputRef}
                    style={{ display: 'none' }}
                    accept='.pdf, .jpeg, .jpg, .png'
                    value={props.value}
                    name={props.name}
                    disabled={isUploadDisabled(selectedFiles, props.maxDocuments || DOCUMENT_CONSTANTS.MAX_DOCUMENTS_PER_PAYMENT)}
                />

                <div>
                    {/* Error Summary */}
                    

                    {selectedFiles.map((file, index) =>
                        props.uploadType === 'uploadIcon' ? (
                            <UploadIconFileRow
                                key={getSelectedFileKey(file)}
                                file={file}
                                index={index}
                                onRemove={handleRemove}
                            />
                        ) : (
                            <FileDetailsRow
                                key={getSelectedFileKey(file)}
                                file={file}
                                index={index}
                                isDeletedFile={isDeletedFile}
                                multiple={props.multiple}
                                isEditable={props.isEditable}
                                documentUlid={props.documentUlid}
                                handleRemove={handleRemove}
                                handleDeleteFile={handleDeleteFile}
                            />
                        )
                    )}
                    {!!errors &&
                        errors.map((file, index) =>
                            file.error ? (
                                <div className='file-div-wrap py-2' key={getErrorFileKey(file)}>
                                    <div className='p-2 rounded' style={{ backgroundColor: '#f8f9fa', border: '1px solid #e9ecef' }}>
                                        <div className='d-flex align-items-center mb-2'>
                                            
                                            <span className='filename-text font-b3'></span>
                                        </div>
                                        <div className='d-flex align-items-center mb-2'>
                                            <i className='bi bi-exclamation-circle-fill text-error-700 me-2'></i>
                                            <span className='text-error-700 font-b3 font-regular'>{file.error}</span>
                                        </div>
                                        <div className='d-flex justify-content-end'>
                                            <span
                                                className='text-secondary-700 remove-btn font-b3 cursor'
                                                onClick={() => handleRemove(index, file)}>
                                                Remove
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            ) : null
                        )}
                    
                </div>
            </div>
        </div>
    );
};

export default FormUpload;
